import { Box, Link, Title } from '@nimbus-ds/components';
import type { ReactElement, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AvisoDePlan } from './AvisoDePlan';
import { useSesion } from '../sesion';

interface Props {
  readonly titulo: string;
  readonly acciones?: ReactNode;
  readonly children: ReactNode;
}

const SECCIONES: ReadonlyArray<{ ruta: string; texto: string }> = [
  { ruta: '/', texto: 'Actualizaciones' },
  { ruta: '/programaciones', texto: 'Programaciones' },
];

/**
 * Las dos páginas públicas del Worker.
 *
 * Van con la ruta absoluta, no relativa: el bundle vive bajo `/admin/` y `soporte` a secas
 * caería en `/admin/soporte`. Y en pestaña nueva porque acá adentro estamos dentro del
 * iframe del panel de Tienda Nube: navegar el iframe a una página nuestra suelta se ve
 * como que la app se rompió.
 */
const PIE: ReadonlyArray<{ href: string; texto: string }> = [
  { href: '/soporte', texto: 'Soporte' },
  { href: '/privacidad', texto: 'Privacidad' },
];

export function Layout({ titulo, acciones, children }: Props): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const sesion = useSesion();

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={{ xs: '4', md: '6' }}
      // En 375px, 16px de padding a cada lado se comen el 8,5% del ancho útil. La mitad
      // alcanza para que el contenido no toque el borde y devuelve 16px a las tarjetas.
      padding={{ xs: '2', md: '4' }}
      /*
        *** El contenido tiene un ancho máximo y se centra. ***

        Estaba en 1200px y pegado al borde izquierdo: en un monitor de 1440 el historial
        dibujaba renglones de más de mil píxeles con medio viewport vacío al costado. Una
        línea así se lee mal —el ojo pierde el renglón al volver— y además no se parece en
        nada al panel de Tienda Nube, que tiene su columna de contenido acotada.

        1040px es lo que necesita la tabla de variantes del detalle, que es lo más ancho
        que muestra la app. Todo lo demás entra sobrado.
      */
      maxWidth="1040px"
      marginX="auto"
      width="100%"
    >
      <Box display="flex" gap="4" alignItems="center">
        {SECCIONES.map((seccion) => (
          <Link
            key={seccion.ruta}
            as="button"
            appearance={location.pathname === seccion.ruta ? 'primary' : 'neutral'}
            onClick={() => navigate(seccion.ruta)}
          >
            {seccion.texto}
          </Link>
        ))}
      </Box>

      <AvisoDePlan sesion={sesion} />

      {/*
        En pantalla ancha el título y las acciones comparten renglón. En un celular no
        entran: el título se corta o los botones se apilan contra el borde. Apilados y a
        lo ancho, la acción principal queda donde llega el pulgar.
      */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={{ xs: '3', md: '2' }}
      >
        <Title as="h1">{titulo}</Title>
        {acciones}
      </Box>

      {children}

      <Box as="footer" display="flex" gap="4" alignItems="center" paddingTop="4">
        {PIE.map((item) => (
          <Link
            key={item.href}
            as="a"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            appearance="neutral"
            fontSize="caption"
          >
            {item.texto}
          </Link>
        ))}
      </Box>
    </Box>
  );
}
